import React, { useState, useEffect, useContext } from "react";

import {
  Factory,
  ArrowRight,
  Sparkles,
  ShieldCheck,
  BarChart3,
  XCircle,
  CheckCircle,
  Download,
  Database,
  Bot,
  Settings,
  LineChart,
  Camera,
  PackageSearch,
  Warehouse,
  Truck,
  ShoppingCart,
  BadgeCheck,
  Wrench,
  Activity,
  Gauge,
  Cpu,
  Boxes,
  ScanSearch,
  ClipboardCheck,
  TrendingUp,
  Workflow,
  LockKeyhole,
  Brain,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import getHelmetContent from "../../Components/Common/HelmetData";

const countryOptions = [
  "Australia",
  "Germany",
  "India",
  "New Zealand",
  "United States",
  "Other",
];

const manufacturingFeatures = [
  {
    id: 1,
    icon: <LineChart size={20} />,
    name: "Production Demand Forecasting & Schedule Optimization",
    purpose: "Production planning / plant operations",
    currentChallenge:
      "Production planning is often handled through Excel sheets, past assumptions and manual coordination across demand, capacity and delivery commitments.",
    aiAction:
      "AI analyzes historical orders, seasonal demand, machine capacity and production constraints to recommend optimized production schedules.",
    benefit:
      "Improves throughput, capacity utilization, production discipline and delivery planning.",
    implementation:
      "Can connect with ERP, order history, Excel production sheets, machine capacity data and dispatch commitments.",
  },
  {
    id: 2,
    icon: <Camera size={20} />,
    name: "Computer Vision for Assembly Line Monitoring",
    purpose: "Assembly line monitoring / shop-floor visibility",
    currentChallenge:
      "Line issues, bottlenecks and abnormal activities are identified late because monitoring depends on manual supervision.",
    aiAction:
      "Computer vision monitors assembly lines for bottlenecks, defects, missing steps and process deviations.",
    benefit:
      "Enables faster issue detection, reduced rework and stronger shop-floor visibility.",
    implementation:
      "Can use existing cameras where feasible or dedicated cameras installed at key production checkpoints.",
  },
  {
    id: 3,
    icon: <Gauge size={20} />,
    name: "Process Optimization AI",
    purpose: "Production process improvement",
    currentChallenge:
      "Process analysis is periodic and manual, making it difficult to identify cycle-time losses and workflow bottlenecks in real time.",
    aiAction:
      "AI analyzes machine, operator, shift and workflow data to recommend better process flow and improvement areas.",
    benefit:
      "Helps reduce cycle time, improve production discipline and support data-backed operational decisions.",
    implementation:
      "Can use machine logs, production records, operator shift data, downtime logs and quality records.",
  },
  {
    id: 4,
    icon: <PackageSearch size={20} />,
    name: "Demand Forecasting AI for Inventory",
    purpose: "Inventory planning / raw material management",
    currentChallenge:
      "Raw material and finished goods planning is often based on manual review, leading to overstock, stockouts or urgent procurement.",
    aiAction:
      "AI predicts material requirements based on consumption trends, production plans, lead times and seasonal demand.",
    benefit:
      "Improves inventory planning, reduces stock pressure and supports better working capital control.",
    implementation:
      "Can connect with stock ledgers, ERP, Excel inventory sheets, purchase history and production planning data.",
  },
  {
    id: 5,
    icon: <Camera size={20} />,
    name: "Automated Stock Counting using Computer Vision + IoT",
    purpose: "Warehouse / store operations",
    currentChallenge:
      "Physical stock counting is manual, time-consuming and prone to mismatch between actual stock and system stock.",
    aiAction:
      "AI-assisted stock counting uses cameras, sensors, barcode, QR or RFID-linked movement data to support verification.",
    benefit:
      "Improves stock accuracy, speeds up audits and reduces dependency on manual counting.",
    implementation:
      "Can be implemented using barcode scanners, RFID, IoT sensors, cameras or hybrid verification workflows.",
  },
  {
    id: 6,
    icon: <Warehouse size={20} />,
    name: "Smart Warehouse Optimization",
    purpose: "Warehouse layout / picking / dispatch",
    currentChallenge:
      "Warehouse teams spend time searching stock, following inefficient picking paths and manually prioritizing dispatch.",
    aiAction:
      "AI suggests optimized storage locations, picking paths, movement patterns and dispatch priorities.",
    benefit:
      "Improves warehouse productivity, order fulfilment speed and dispatch discipline.",
    implementation:
      "Can work with bin locations, SKU movement data, dispatch priority, stock aging and warehouse layout information.",
  },
  {
    id: 7,
    icon: <Truck size={20} />,
    name: "Supplier Risk Analytics",
    purpose: "Procurement / vendor management",
    currentChallenge:
      "Supplier performance is reviewed manually, making it difficult to identify vendor risk early.",
    aiAction:
      "AI analyzes supplier delivery delays, rejection rates, pricing history, responsiveness and dependency levels.",
    benefit:
      "Improves vendor control, reduces supply disruption and supports alternative supplier planning.",
    implementation:
      "Can generate supplier scorecards using purchase orders, GRN records, rejection data and delivery history.",
  },
  {
    id: 8,
    icon: <TrendingUp size={20} />,
    name: "Raw Material Price Prediction Models",
    purpose: "Procurement planning / purchase timing",
    currentChallenge:
      "Raw material pricing is tracked manually, which makes purchase timing decisions dependent on limited visibility.",
    aiAction:
      "AI tracks price trends, purchase history and market indicators to suggest better buying windows.",
    benefit:
      "Supports smarter procurement decisions and reduces exposure to unfavorable price movement.",
    implementation:
      "Can use purchase history, supplier quotes, commodity price trends and market reference data.",
  },
  {
    id: 9,
    icon: <ShoppingCart size={20} />,
    name: "AI-Powered Purchase Automation",
    purpose: "Purchase request / PO workflow",
    currentChallenge:
      "Purchase requests and purchase orders are manually prepared, checked and followed up for approval.",
    aiAction:
      "AI auto-generates purchase request or PO drafts based on stock levels, production demand and approval rules.",
    benefit:
      "Improves procurement cycle speed, reduces manual errors and creates better purchase discipline.",
    implementation:
      "Can connect with stock thresholds, supplier master, approval hierarchy, ERP purchase module and inventory planning data.",
  },
  {
    id: 10,
    icon: <BadgeCheck size={20} />,
    name: "Computer Vision Quality Inspection",
    purpose: "Quality control / visual inspection",
    currentChallenge:
      "Manual inspection can miss visual defects, especially during high-volume production or repetitive inspection work.",
    aiAction:
      "Computer vision detects cracks, dents, missing components, shape issues, color mismatch or assembly defects.",
    benefit:
      "Improves quality consistency, reduces rework and identifies defects closer to the production stage.",
    implementation:
      "Can be deployed at inspection points using cameras, image datasets, defect labels and review workflows.",
  },
  {
    id: 11,
    icon: <Activity size={20} />,
    name: "Quality Anomaly Detection AI",
    purpose: "Quality analytics / defect prevention",
    currentChallenge:
      "Quality trends are often reviewed after defects increase, causing delayed corrective action.",
    aiAction:
      "AI monitors quality, machine and process parameters to detect abnormal patterns before defects rise.",
    benefit:
      "Supports early defect detection, reduced scrap risk and stronger process control.",
    implementation:
      "Can use inspection results, rejection logs, machine parameters, shift data and supplier lot information.",
  },
  {
    id: 12,
    icon: <Settings size={20} />,
    name: "Root Cause Analysis AI",
    purpose: "Defect analysis / corrective action",
    currentChallenge:
      "Root cause analysis takes time because teams manually correlate defects with machines, materials, operators and process changes.",
    aiAction:
      "AI correlates defect patterns with machine settings, supplier lots, shifts, operators and process events.",
    benefit:
      "Speeds up corrective action, supports evidence-based RCA and reduces repeat defects.",
    implementation:
      "Can analyze defect records, production batches, shift logs, maintenance history and quality inspection data.",
  },
  {
    id: 13,
    icon: <Wrench size={20} />,
    name: "Predictive Maintenance",
    purpose: "Maintenance planning / machine reliability",
    currentChallenge:
      "Maintenance teams often respond after machine breakdowns, causing unplanned downtime and production disruption.",
    aiAction:
      "AI predicts likely machine failure using sensor readings, maintenance history and usage patterns.",
    benefit:
      "Reduces unplanned downtime, improves machine availability and supports planned maintenance decisions.",
    implementation:
      "Can use vibration, temperature, energy, runtime, breakdown logs and maintenance records.",
  },
  {
    id: 14,
    icon: <Activity size={20} />,
    name: "Condition Monitoring AI",
    purpose: "Machine health monitoring",
    currentChallenge:
      "Machine health checks are performed through manual rounds or periodic inspections, leaving gaps between checks.",
    aiAction:
      "AI continuously monitors machine health signals and raises alerts when abnormal behavior is detected.",
    benefit:
      "Improves equipment lifespan, reduces emergency repairs and supports condition-based maintenance.",
    implementation:
      "Can connect with IoT sensors, PLC data, machine logs, temperature data, vibration data and energy usage.",
  },
  {
    id: 15,
    icon: <Wrench size={20} />,
    name: "AI-Driven Maintenance Scheduling",
    purpose: "Maintenance scheduling / service planning",
    currentChallenge:
      "Maintenance schedules are often fixed or manually planned without considering actual machine usage and production priority.",
    aiAction:
      "AI recommends maintenance schedules based on machine usage, risk level, breakdown probability and production plans.",
    benefit:
      "Reduces over-servicing, avoids under-servicing and improves maintenance planning efficiency.",
    implementation:
      "Can work with maintenance calendars, machine usage, spare availability, production schedules and downtime windows.",
  },
];

const featureCategories = [
  "All",
  "Production",
  "Inventory",
  "Procurement",
  "Quality",
  "Maintenance",
  "Computer Vision",
];

const getFeatureCategory = (purpose, name) => {
  const text = `${purpose} ${name}`.toLowerCase();

  if (
    text.includes("quality") ||
    text.includes("defect") ||
    text.includes("root cause") ||
    text.includes("inspection")
  ) {
    return "Quality";
  }

  if (
    text.includes("maintenance") ||
    text.includes("machine health") ||
    text.includes("condition")
  ) {
    return "Maintenance";
  }

  if (
    text.includes("vision") ||
    text.includes("camera") ||
    text.includes("assembly line")
  ) {
    return "Computer Vision";
  }

  if (
    text.includes("inventory") ||
    text.includes("warehouse") ||
    text.includes("stock")
  ) {
    return "Inventory";
  }

  if (
    text.includes("supplier") ||
    text.includes("procurement") ||
    text.includes("purchase") ||
    text.includes("raw material price")
  ) {
    return "Procurement";
  }

  return "Production";
};

const valueAreas = [
  {
    icon: <Factory size={24} />,
    title: "Production Planning",
    text: "Forecast demand, optimize schedules, identify bottlenecks and improve capacity utilization.",
  },
  {
    icon: <PackageSearch size={24} />,
    title: "Inventory & Warehouse",
    text: "Improve stock visibility, reduce stock mismatches, optimize picking paths and support better dispatch planning.",
  },
  {
    icon: <BadgeCheck size={24} />,
    title: "Quality Intelligence",
    text: "Detect defects earlier, identify abnormal patterns and speed up root cause analysis.",
  },
  {
    icon: <Wrench size={24} />,
    title: "Maintenance Reliability",
    text: "Predict failures, monitor machine health and schedule maintenance based on real operating conditions.",
  },
];

const implementationAreas = [
  {
    icon: <Database size={22} />,
    title: "Connect Existing Systems",
    text: "AI can work with ERP, Excel sheets, SQL databases, machine logs, warehouse records, purchase data and quality records.",
  },
  {
    icon: <Camera size={22} />,
    title: "Use Cameras & IoT Where Needed",
    text: "Computer vision and sensor-based features can be added gradually for inspection, stock counting and machine monitoring.",
  },
  {
    icon: <Bot size={22} />,
    title: "Create Operations AI Assistants",
    text: "Role-based AI assistants can support plant managers, quality teams, procurement, warehouse and maintenance teams.",
  },
  {
    icon: <ShieldCheck size={22} />,
    title: "Secure Plant Data",
    text: "Private deployment, user roles, audit logs and controlled access can protect operational and production data.",
  },
];

const consultationPoints = [
  "Improve production visibility",
  "Optimize inventory planning",
  "Strengthen quality control",
  "Enable predictive maintenance",
];

const ManufacturingAI = () => {
  const [selectedCountry, setSelectedCountry] = useState("India");
  const [activeCategory, setActiveCategory] = useState("All");

  const country = useContext(CountryContext);
  const websiteURL = window.location.origin;

  const [formVisible, setFormVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [formError, setFormError] = useState("");
  const [phone, setPhone] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    country: "",
    message: "",
    firmType: "Manufacturing",
  });

  const [errors, setErrors] = useState({
    name: "",
    email: "",
    phone: "",
    country: "",
    firmType: "",
    message: "",
  });

  useEffect(() => {
    AOS.init({
      duration: 900,
      easing: "ease-out-cubic",
      once: true,
      offset: 80,
    });
  }, []);

  useEffect(() => {
    if (countryOptions.includes(country)) {
      setSelectedCountry(country);
    } else {
      setSelectedCountry("India");
    }
  }, [country]);

  const filteredFeatures =
    activeCategory === "All"
      ? manufacturingFeatures
      : manufacturingFeatures.filter(
          (item) => getFeatureCategory(item.purpose, item.name) === activeCategory
        );

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData({
      ...formData,
      [name]: value,
    });

    setErrors({
      ...errors,
      [name]: "",
    });

    if (name === "country") {
      setSelectedCountry(value);
      setPhone("");

      setErrors((prev) => ({
        ...prev,
        phone: "",
        country: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    let newErrors = {
      name: "",
      email: "",
      phone: "",
      country: "",
      firmType: "",
      message: "",
    };

    let isValid = true;

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
      isValid = false;
    }

    const emailRegex = /^\S+@\S+\.\S+$/;

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email.trim())) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }

    if (!phone) {
      newErrors.phone = "Phone number is required";
      isValid = false;
    } else if (!isValidPhoneNumber(phone)) {
      newErrors.phone = "Please enter a valid phone number";
      isValid = false;
    }

    if (!selectedCountry) {
      newErrors.country = "Please select country";
      isValid = false;
    }

    if (formData.message.trim().length > 300) {
      newErrors.message = "Maximum character limit is 300";
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) return;

    const payload = {
      name: formData.name,
      email: formData.email.trim(),
      phone: phone,
      lookingFor: "Manufacturing",
      queryPageIndex: "MANUFACTURING_AI_PAGE",
      message:
        (formData.message || "") +
        ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
    };

    try {
      setLoading(true);
      setFormError("");

      const response = await fetch(
        ApiPath + "/api/enquiryController/tqAiEnquiry",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(payload),
        }
      );

      if (response.ok) {
        setFormData({
          name: "",
          email: "",
          firmType: "Manufacturing",
          message: "",
        });

        if (window.fbq) {
          window.fbq("track", "Lead");
        }

        setPhone("");
        setFormVisible(false);

        setTimeout(() => {
          setFormVisible(true);
          setLoading(false);
        }, 5000);
      } else {
        setFormError("Something went wrong. Please try again.");
        setLoading(false);
      }
    } catch (error) {
      console.error(error);
      setFormError("Something went wrong. Please try again.");
      setLoading(false);
    }
  };

  const getCountryCode = (countryName) => {
    const map = {
      India: "IN",
      "United States": "US",
      Australia: "AU",
      Germany: "DE",
      "New Zealand": "NZ",
    };

    return map[countryName] || "IN";
  };

  const StatCard = ({ icon, label, value, subText }) => (
    <div className="group rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)] lg:p-7">
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
        {icon}
      </div>

      <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
        {label}
      </p>

      <h3 className="mt-3 text-2xl font-black leading-tight text-slate-900">
        {value}
      </h3>

      <p className="mt-3 text-sm leading-7 text-slate-500">{subText}</p>
    </div>
  );

  const pageTitle =
    "AI Automation For Manufacturing Operations - ThinkQuotient AI";
  const metaTitle =
    "AI Automation For Manufacturing Operations - ThinkQuotient AI";
  const helmetContent = getHelmetContent();

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={metaTitle} />

        {helmetContent.meta.map((metaTag, index) => (
          <meta key={index} name={metaTag.name} content={metaTag.content} />
        ))}

        {helmetContent.script}

        <script>
          {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-KTNBVH9V');
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=UA-143016865-1"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || []; 
            function gtag() { 
              dataLayer.push(arguments); 
            } 
            gtag("js", new Date()); 
            gtag("config", "UA-143016865-1");
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=G-BSFPPN5E9D"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'G-BSFPPN5E9D');
          `}
        </script>

        <script
          async
          src="https://www.googletagmanager.com/gtag/js?id=AW-574047969"
        ></script>
        <script>
          {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'AW-574047969');
          `}
        </script>

        <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTNBVH9V" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>

        <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1317328810324214');
          fbq('track', 'PageView');
        `}</script>

        <noscript>{`<img height="1" width="1" style="display:none"
          src="https://www.facebook.com/tr?id=1317328810324214&ev=PageView&noscript=1"
        />`}</noscript>
      </Helmet>

      <Head />

      <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900">
        {/* HERO */}
        <section
          className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-16 sm:py-20 lg:py-12"
          style={{
            backgroundImage:
              "url('/assets/images/3d-rendering-robot-hand-pointing-finger.jpg')",
          }}
        >
          <div className="absolute inset-0 bg-slate-950/40"></div>
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/60 via-blue-950/45 to-slate-950/75"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/30 via-transparent to-slate-950/60"></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="100"
            className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-cyan-400/10 blur-3xl"
          ></div>

          <div
            data-aos="zoom-in"
            data-aos-delay="250"
            className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl"
          ></div>

          <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 items-center gap-12 xl:gap-20 lg:grid-cols-12">
              <div
                className="text-center lg:col-span-7 xl:col-span-7 lg:text-left"
                data-aos="fade-right"
              >
                <div
                  data-aos="fade-up"
                  data-aos-delay="100"
                  className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-md"
                >
                  <Sparkles size={16} className="text-cyan-300" />
                  AI Solutions For Manufacturing
                </div>

                <h1
                  data-aos="fade-up"
                  data-aos-delay="200"
                  className="mt-8 text-4xl font-black leading-[1.02] tracking-[-0.04em] text-white sm:text-5xl xl:text-5xl"
                >
                  AI Automation
                  <span className="mt-3 block bg-gradient-to-r from-cyan-300 via-blue-200 to-white bg-clip-text py-2 text-transparent">
                    For Smart Manufacturing Operations
                  </span>
                </h1>

                <p
                  data-aos="fade-up"
                  data-aos-delay="300"
                  className="mx-auto mt-5 max-w-2xl text-base leading-8 text-white/80 sm:text-lg lg:mx-0"
                >
                  Improve manufacturing operations with AI-enabled production
                  planning, inventory intelligence, warehouse optimization,
                  computer vision inspection, supplier analytics, predictive
                  maintenance and plant-level decision support.
                </p>

                <div
                  data-aos="fade-up"
                  data-aos-delay="400"
                  className="mt-10 flex flex-col justify-center gap-4 sm:flex-row sm:flex-wrap lg:justify-start"
                >
                  <a
                    href="#contact-form"
                    className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-bold text-slate-950 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-cyan-50"
                  >
                    Get AI Consultation
                    <ArrowRight
                      size={18}
                      className="transition-transform duration-300 group-hover:translate-x-1"
                    />
                  </a>

                  <a
                    href="/assets/pdfs/AI Feature Benefit Report for Manufacturing Industry.pdf"
                    download
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                  >
                    Download Report
                    <Download size={18} />
                  </a>
                </div>
              </div>

              <div
                className="relative lg:col-span-5 xl:col-span-5"
                data-aos="fade-left"
                data-aos-delay="250"
              >
                <div className="absolute -inset-4 rounded-[2.8rem] bg-cyan-400/15 blur-2xl"></div>

                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/20 bg-white/10 p-4 shadow-[0_30px_90px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                  <img
                    src="/assets/images/manufacturingAi.png"
                    alt="Manufacturing AI"
                    className="h-[360px] w-full rounded-[2rem] object-cover sm:h-[430px]"
                  />

                  <div className="absolute inset-4 rounded-[2rem] bg-gradient-to-t from-slate-950/70 via-transparent to-transparent"></div>

                  <div className="absolute inset-x-8 bottom-8 rounded-3xl border border-white/20 bg-slate-950/45 p-5 text-white backdrop-blur-xl">
                    <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-bold text-cyan-200">
                      <Factory size={13} />
                      Smart Factory AI
                    </div>

                    <p className="mt-3 text-base font-bold sm:text-lg">
                      AI layer for plant operations, not an ERP replacement.
                    </p>

                    <p className="mt-2 text-sm leading-6 text-white/75">
                      Works with ERP, Excel sheets, machine data, warehouse
                      records and production workflows.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* AI FEATURES SECTION */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="grid items-start gap-10 lg:grid-cols-12">
              <div className="lg:col-span-8" data-aos="fade-right">
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-5 py-3 text-sm font-bold text-blue-700 shadow-sm">
                  <BarChart3 size={16} />
                  AI Feature Benefits
                </div>

                <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                  Practical AI Modules For Manufacturing Teams
                </h2>

                <p className="mt-6 max-w-3xl text-lg leading-9 text-slate-600">
                  Explore AI modules that support production planning, quality
                  control, procurement, warehouse, maintenance, inventory and
                  plant management teams.
                </p>
              </div>

              <div className="lg:col-span-4" data-aos="fade-left">
                <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-[0_20px_70px_rgba(15,23,42,0.08)]">
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                    <ShieldCheck size={26} />
                  </div>

                  <h3 className="mt-5 text-xl font-black text-slate-900">
                    AI Layer, Not ERP Replacement
                  </h3>

                  <p className="mt-3 text-sm leading-7 text-slate-600">
                    The AI system works as an operations layer on top of ERP,
                    Excel sheets, SQL databases, machine logs, camera feeds,
                    warehouse data and quality records.
                  </p>
                </div>
              </div>
            </div>

            {/* Category Filters */}
            <div className="mt-12 flex flex-wrap gap-3" data-aos="fade-up">
              {featureCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${
                    activeCategory === category
                      ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                      : "border border-slate-200 bg-white text-slate-600 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-700"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            {/* Feature Cards */}
            <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
              {filteredFeatures.map((item, index) => (
                <div
                  key={item.id}
                  data-aos="fade-up"
                  data-aos-delay={(index % 6) * 80}
                  className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_80px_rgba(15,23,42,0.10)]"
                >
                  <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                  <div className="flex items-start justify-between gap-4">
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                      {item.icon}
                    </div>

                    <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-bold text-slate-500">
                      {getFeatureCategory(item.purpose, item.name)}
                    </span>
                  </div>

                  <h3 className="mt-5 text-lg font-black leading-7 text-slate-900">
                    {item.name}
                  </h3>

                  <p className="mt-3 text-sm font-semibold text-blue-700">
                    {item.purpose}
                  </p>

                  <div className="mt-5 space-y-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        Current Challenge
                      </p>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {item.currentChallenge}
                      </p>
                    </div>

                    <div>
                      <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                        What AI Does
                      </p>
                      <p className="mt-2 text-sm leading-7 text-slate-600">
                        {item.aiAction}
                      </p>
                    </div>
                  </div>

                  <div className="mt-6 rounded-2xl bg-blue-50 p-4">
                    <div className="flex gap-3">
                      <CheckCircle
                        size={18}
                        className="mt-0.5 shrink-0 text-blue-600"
                      />
                      <p className="text-sm font-semibold leading-7 text-blue-800">
                        {item.benefit}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <p className="mt-6 text-xs leading-6 text-slate-500">
              *The above modules are for solution planning. Final
              implementation scope depends on plant size, machine count,
              ERP/database availability, camera/sensor readiness, data quality,
              user roles and operational workflow requirements.
            </p>
          </div>
        </section>

        {/* VALUE AREAS */}
        <section className="bg-white py-20">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700">
                <Sparkles size={16} />
                AI Opportunity Areas
              </div>

              <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                Where AI Creates Value In Manufacturing
              </h2>

              <p className="mt-6 text-lg leading-9 text-slate-600">
                Start with practical plant workflows and then expand into
                advanced production, quality, warehouse and maintenance
                intelligence.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {valueAreas.map((item, index) => (
                <div
                  key={index}
                  className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {item.icon}
                  </div>

                  <h3 className="mt-6 text-xl font-black text-slate-900">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* IMPLEMENTATION APPROACH */}
        <section className="relative overflow-hidden py-20">
          <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
          <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

          <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
              <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-5 py-3 text-sm font-bold text-blue-700 shadow-sm">
                <Workflow size={16} />
                Implementation Approach
              </div>

              <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                AI Layer On Top Of Existing Manufacturing Systems
              </h2>

              <p className="mt-6 text-lg leading-9 text-slate-600">
                The solution can be implemented in phases without disturbing
                existing ERP, Excel, production, inventory and maintenance
                workflows.
              </p>
            </div>

            <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
              {implementationAreas.map((item, index) => (
                <div
                  key={index}
                  className="group rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                    {item.icon}
                  </div>

                  <h3 className="mt-6 text-xl font-black text-slate-900">
                    {item.title}
                  </h3>

                  <p className="mt-4 text-sm leading-7 text-slate-600">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>

            <div
              data-aos="fade-up"
              data-aos-delay="300"
              className="mt-12 rounded-[2rem] border border-blue-100 bg-blue-50/70 p-6"
            >
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <LockKeyhole size={22} />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900">
                      Controlled Access
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      Role-wise access for plant managers, quality, warehouse,
                      procurement and maintenance teams.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <Brain size={22} />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900">
                      Assistive Intelligence
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      AI supports planning, recommendations and alerts while
                      teams retain operational control.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                    <Boxes size={22} />
                  </div>
                  <div>
                    <h3 className="font-black text-slate-900">
                      Modular Rollout
                    </h3>
                    <p className="mt-2 text-sm leading-7 text-slate-600">
                      Start with one plant workflow and scale across production,
                      quality, inventory and maintenance.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CONTACT FORM SECTION */}
        <section
          id="contact-form"
          className="relative overflow-hidden py-20 sm:py-24"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

          <div className="relative mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 md:px-4">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 xl:grid-cols-5">
              <div
                className="px-6 lg:col-span-1 xl:col-span-3"
                data-aos="fade-right"
              >
                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700">
                  <Sparkles size={16} />
                  AI Consultation For Manufacturing
                </div>

                <h2 className="mt-6 text-4xl font-black leading-tight text-slate-900 sm:text-5xl">
                  Let’s Build
                  <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    AI For Your Manufacturing Business
                  </span>
                </h2>

                <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
                  Automate production workflows, improve inventory visibility,
                  strengthen quality inspection, reduce operational bottlenecks
                  and modernize manufacturing processes using AI-powered systems.
                </p>

                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                  {consultationPoints.map((item, index) => (
                    <div
                      key={index}
                      data-aos="fade-up"
                      data-aos-delay={index * 100}
                      className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
                    >
                      <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                        <CheckCircle size={18} />
                      </div>

                      <span className="font-medium text-slate-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div
                className="relative lg:col-span-1 xl:col-span-2"
                data-aos="fade-left"
                data-aos-delay="100"
              >
                <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-r from-blue-500 to-indigo-600 opacity-20 blur-xl"></div>

                <div className="relative overflow-hidden border border-white/40 bg-white/90 shadow-[0_25px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl md:rounded-[2rem]">
                  <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                    <div className="flex items-center gap-4">
                      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md">
                        <Factory size={28} />
                      </div>
                      <div>
                        <h3 className="text-2xl font-bold">
                          Book AI Consultation
                        </h3>
                        <p className="mt-1 text-sm text-blue-100">
                          Tell us about your manufacturing automation
                          requirements
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="p-6 sm:p-8">
                    {!formVisible && (
                      <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-6 text-blue-700">
                        <div className="flex flex-col items-center text-center">
                          <CheckCircle
                            size={52}
                            className="mb-4 text-blue-600"
                          />
                          <h3 className="mb-2 text-xl font-bold">
                            Thank You!
                          </h3>
                          <p className="text-sm sm:text-base">
                            Our team will contact you shortly.
                          </p>
                        </div>
                      </div>
                    )}

                    {formError && (
                      <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-6 text-red-700">
                        <div className="flex flex-col items-center text-center">
                          <XCircle
                            size={48}
                            className="mb-3 text-red-500"
                          />
                          <h3 className="mb-2 text-lg font-bold">
                            Submission Failed
                          </h3>
                          <p className="text-sm">{formError}</p>
                        </div>
                      </div>
                    )}

                    {formVisible && (
                      <form onSubmit={handleSubmit} className="space-y-5">
                        <div>
                          <input
                            type="text"
                            name="name"
                            placeholder="Full Name"
                            value={formData.name}
                            onChange={handleChange}
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.name
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.name && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.name}
                            </p>
                          )}
                        </div>

                        <div>
                          <div
                            className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${
                              errors.phone
                                ? "border-red-500"
                                : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus-within:ring-blue-100"
                            }`}
                          >
                            <PhoneInput
                              key={selectedCountry}
                              countries={[
                                "IN",
                                "US",
                                "AU",
                                "DE",
                                "JP",
                                "SG",
                                "NZ",
                              ]}
                              defaultCountry={getCountryCode(selectedCountry)}
                              international
                              value={phone}
                              onChange={(value) => {
                                setPhone(value || "");
                                setErrors({ ...errors, phone: "" });
                              }}
                              placeholder="Enter phone number"
                            />
                          </div>
                          {errors.phone && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.phone}
                            </p>
                          )}
                        </div>

                        <div>
                          <input
                            type="email"
                            name="email"
                            placeholder="Email Address"
                            value={formData.email}
                            onChange={handleChange}
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.email
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.email && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.email}
                            </p>
                          )}
                        </div>

                        <div>
                          <select
                            id="country"
                            name="country"
                            className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.country
                                ? "border-red-500"
                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                            onChange={handleChange}
                            value={selectedCountry}
                          >
                            {countryOptions.map((countryName) => (
                              <option key={countryName} value={countryName}>
                                {countryName}
                              </option>
                            ))}
                          </select>
                          {errors.country && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.country}
                            </p>
                          )}
                        </div>

                        <div>
                          <textarea
                            rows={3}
                            name="message"
                            placeholder="Tell us which manufacturing workflows, operational bottlenecks, quality issues, inventory processes or factory systems you want to automate"
                            value={formData.message}
                            onChange={handleChange}
                            className={`w-full resize-none rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${
                              errors.message
                                ? "border border-red-500"
                                : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                            }`}
                          />
                          {errors.message && (
                            <p className="mt-2 text-sm text-red-500">
                              {errors.message}
                            </p>
                          )}
                        </div>

                        <button
                          type="submit"
                          disabled={loading}
                          className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${
                            loading
                              ? "cursor-not-allowed bg-slate-400"
                              : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                          }`}
                        >
                          {loading
                            ? "Submitting..."
                            : "Request AI Consultation"}
                          {!loading && <ArrowRight size={18} />}
                        </button>
                      </form>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <FooterNew />
      </div>
    </>
  );
};

export default ManufacturingAI;